import { useState, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/shared/Pagination';
import { fmtMoeda } from '@/lib/utils/formatters';
import {
  STATUS_PRODUTO_LABEL,
  type SegregacaoCalcularResponseData,
  type StatusProduto,
} from '@/services/SimplesNacional/Segregacao/api/types';

export interface ProdutoUnificado {
  Produto: string;
  EAN: string;
  Descricao: string;
  Status: string;
  NCM: string;
  ValTot: number;
  TipoItem?: string;
  IDSegmento?: string;
}

export function buildProdutosUnificados(
  segregacao: SegregacaoCalcularResponseData
): ProdutoUnificado[] {
  return [
    ...(segregacao.IGN ?? []).map(p => ({ ...p, ValTot: p.ValTot as number })),
    ...(segregacao.REV ?? []).map(p => ({ ...p, ValTot: p.ValTot as number })),
    ...(segregacao.SEG ?? []).map(p => ({
      ...p,
      ValTot: p.ValTot as number,
      TipoItem: p.TipoItem,
      IDSegmento: p.IDSegmento,
    })),
  ];
}

interface Props {
  segregacao: SegregacaoCalcularResponseData;
  statusFiltro: string | null;
  selected: string[];
  onSelectionChange: (produtos: string[]) => void;
}

export function ProdutosFiltradosTable({
  segregacao,
  statusFiltro,
  selected,
  onSelectionChange,
}: Props) {
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(25);

  const todos = useMemo(() => buildProdutosUnificados(segregacao), [segregacao]);

  const filtrados = useMemo(() => {
    let lista = statusFiltro ? todos.filter(p => p.Status === statusFiltro) : todos;
    if (busca) {
      const q = busca.toLowerCase();
      lista = lista.filter(
        p =>
          p.Produto?.toLowerCase().includes(q) ||
          p.Descricao?.toLowerCase().includes(q) ||
          p.NCM?.includes(q)
      );
    }
    return lista;
  }, [todos, statusFiltro, busca]);

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / itensPorPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * itensPorPagina;
  const itens = filtrados.slice(inicio, inicio + itensPorPagina);

  const todosNaPaginaSelecionados =
    itens.length > 0 && itens.every(p => selected.includes(p.Produto));

  const handleToggleAll = () => {
    if (todosNaPaginaSelecionados) {
      onSelectionChange(selected.filter(s => !itens.some(p => p.Produto === s)));
    } else {
      const novos = itens.map(p => p.Produto).filter(c => !selected.includes(c));
      onSelectionChange([...selected, ...novos]);
    }
  };

  const handleToggle = (produto: string) => {
    if (selected.includes(produto)) {
      onSelectionChange(selected.filter(s => s !== produto));
    } else {
      onSelectionChange([...selected, produto]);
    }
  };

  if (filtrados.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-muted-foreground text-center">
        {statusFiltro ? 'Nenhum produto com o status selecionado.' : 'Nenhum produto encontrado.'}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="px-4 pt-2 flex items-center gap-3">
        <Input
          placeholder="Buscar produto, descrição ou NCM..."
          value={busca}
          onChange={e => {
            setBusca(e.target.value);
            setPagina(1);
          }}
          className="max-w-sm h-8 text-xs"
        />
        {selected.length > 0 && (
          <span className="text-xs text-muted-foreground">{selected.length} selecionado(s)</span>
        )}
        {selected.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-muted-foreground"
            onClick={() => onSelectionChange([])}
          >
            Limpar seleção
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="px-4 py-2.5 w-10">
                <Checkbox
                  checked={todosNaPaginaSelecionados}
                  onCheckedChange={handleToggleAll}
                  aria-label="Selecionar todos na página"
                />
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Produto</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Descrição</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">NCM</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
              <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">
                Valor Total
              </th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item, i) => {
              const statusInfo = STATUS_PRODUTO_LABEL[item.Status as StatusProduto];
              const isSelected = selected.includes(item.Produto);
              return (
                <tr
                  key={i}
                  className={`border-b last:border-0 transition-colors cursor-pointer ${
                    isSelected ? 'bg-accent/30' : 'hover:bg-muted/20'
                  }`}
                  onClick={() => handleToggle(item.Produto)}
                >
                  <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => handleToggle(item.Produto)}
                    />
                  </td>
                  <td className="px-4 py-2 max-w-[130px] truncate" title={item.Produto}>
                    {item.Produto || '—'}
                  </td>
                  <td className="px-4 py-2 max-w-[200px] truncate" title={item.Descricao}>
                    {item.Descricao || '—'}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{item.NCM || '—'}</td>
                  <td className="px-4 py-2">
                    {statusInfo ? (
                      <Badge className={`${statusInfo.color} text-white text-[10px] px-1.5 py-0`}>
                        {statusInfo.label}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">{item.Status || '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {fmtMoeda(item.ValTot)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-4 pb-2 flex items-center justify-between gap-4 text-xs text-muted-foreground">
        <span>{filtrados.length} produto(s)</span>
        {totalPaginas > 1 && (
          <Pagination
            currentPage={paginaAtual}
            totalPages={totalPaginas}
            onPageChange={setPagina}
            itensPorPagina={itensPorPagina}
            onItensPorPaginaChange={v => {
              setItensPorPagina(v);
              setPagina(1);
            }}
          />
        )}
      </div>
    </div>
  );
}
